import { Instagram, Youtube, Twitter, Mail } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { INSTAGRAM_URL, APP_STORE_URL, GOOGLE_PLAY_URL } from "@/components/landing/StoreButtons";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/landing/LanguageSelector";

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: INSTAGRAM_URL },
  { icon: Youtube, label: "YouTube", href: "#" },
  { icon: Twitter, label: "Twitter", href: "#" },
  { icon: Mail, label: "Email", href: "mailto:support@motogo.life" },
];

export const Footer = () => {
  const { t } = useLanguage();
  const GROUPS = {
    [t.footer.gProduct]: [
      { label: t.footer.lFeatures, href: "#features" },
      { label: t.footer.lApp, href: "#app-preview" },
      { label: t.footer.lMap, href: "#map" },
      { label: t.footer.lDownload, href: "#download" },
    ],
    [t.footer.gCompany]: [
      { label: t.footer.lBusiness, href: "#for-business" },
      { label: t.footer.lCommunity, href: "#community" },
      { label: t.footer.lContact, href: "mailto:support@motogo.life" },
    ],
    [t.footer.gApps]: [
      { label: t.footer.lAppStore, href: APP_STORE_URL },
      { label: t.footer.lPlay, href: GOOGLE_PLAY_URL },
      { label: t.footer.lInstagram, href: INSTAGRAM_URL },
    ],
  };
  const handleClick = (href) => {
    if (href.startsWith("#") && href.length > 1) {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    } else if (href.startsWith("mailto:")) {
      window.location.href = href;
    } else if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <footer data-testid="footer-section" className="relative border-t border-white/8 bg-[#07090B] pt-16 pb-10">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14]/35 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-3 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
              <Logo className="h-12 w-12" />
            </div>
            <p className="mt-4 text-sm text-white/60 max-w-sm leading-relaxed">{t.footer.tagline}</p>
            <div data-testid="footer-social-links" className="mt-6 flex items-center gap-2">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                const external = s.href.startsWith("http");
                return (
                  <a key={s.label} href={s.href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    aria-label={s.label} data-testid={`footer-social-${s.label.toLowerCase()}`}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 bg-white/[0.04] text-white/75 hover:text-white hover:bg-white/[0.08] hover:border-white/16 hover:shadow-[0_0_0_1px_rgba(57,255,20,0.25),0_0_18px_rgba(57,255,20,0.18)] transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
            <div className="mt-6">
              <LanguageSelector />
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {Object.entries(GROUPS).map(([group, items]) => (
              <div key={group}>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">{group}</div>
                <ul className="space-y-2.5">
                  {items.map((it) => (
                    <li key={it.label}>
                      <button onClick={() => handleClick(it.href)}
                        className="text-sm text-white/70 hover:text-white hover:[text-shadow:0_0_12px_rgba(57,255,20,0.35)] transition-colors">
                        {it.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/55">
            © {new Date().getFullYear()} MotoGO. {t.footer.copyright}
          </div>
          <div className="font-display font-semibold text-sm text-white/85 text-center">
            {t.footer.slogan1} <span className="text-[#39FF14]">{t.footer.slogan2}</span>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/55">
            Made by <span className="text-white/85 font-semibold normal-case tracking-normal">Marco Ghiriti</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
